"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, Alert, Badge, Button } from "react-bootstrap"
import { apiRequest, endpoints } from "../utils/api"
import { toast } from "react-hot-toast"

const TemperatureMonitor = ({ location, crops }) => {
  const [temperatureAlerts, setTemperatureAlerts] = useState([])
  const [currentWeather, setCurrentWeather] = useState(null)
  const [monitoring, setMonitoring] = useState(false)

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken")
    return {
      Authorization: `Bearer ${token}`,
    }
  }

  // ✅ define checkTemperatureAlerts with useCallback so it’s stable
  const checkTemperatureAlerts = useCallback(async () => {
    if (!location || !crops || crops.length === 0) return

    try {
      setMonitoring(true)

      const weatherResponse = await apiRequest(
        `${endpoints.weather}?lat=${location.lat}&lng=${location.lng}&years=1`,
      )

      if (weatherResponse.data && weatherResponse.data.length > 0) {
        const latestWeather = weatherResponse.data[weatherResponse.data.length - 1]
        setCurrentWeather(latestWeather)

        const alerts = []
        const currentTemp = latestWeather.avgTemp

        for (const crop of crops) {
          const optimalRange = { min: crop.min_temp, max: crop.max_temp }
          const isOutOfRange = currentTemp < optimalRange.min || currentTemp > optimalRange.max

          if (isOutOfRange) {
            const severity =
              Math.abs(currentTemp - (currentTemp < optimalRange.min ? optimalRange.min : optimalRange.max)) > 10
                ? "critical"
                : Math.abs(currentTemp - (currentTemp < optimalRange.min ? optimalRange.min : optimalRange.max)) > 5
                ? "warning"
                : "info"

            alerts.push({
              crop,
              currentTemp,
              optimalRange,
              severity,
              type: currentTemp < optimalRange.min ? "cold" : "hot",
            })

            // Send notification
            try {
              await apiRequest("/api/admin/notifications/temperature-alert", {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                  cropName: crop.name,
                  currentTemp,
                  optimalRange,
                  location: { lat: location.lat, lng: location.lng, name: location.name },
                  severity,
                }),
              })
            } catch (notifError) {
              console.error("Error creating temperature alert:", notifError)
            }
          }
        }

        setTemperatureAlerts(alerts)

        if (alerts.length > 0) {
          const criticalAlerts = alerts.filter((a) => a.severity === "critical")
          if (criticalAlerts.length > 0) {
            toast.error(`Critical temperature alerts for ${criticalAlerts.length} crop(s)!`)
          }
        }
      }
    } catch (error) {
      console.error("Error checking temperature alerts:", error)
    } finally {
      setMonitoring(false)
    }
  }, [location, crops])

  // ✅ useEffect uses stable checkTemperatureAlerts
  useEffect(() => {
    if (location && crops && crops.length > 0) {
      checkTemperatureAlerts()
      const interval = setInterval(checkTemperatureAlerts, 10 * 60 * 1000) // every 10 min
      return () => clearInterval(interval)
    }
  }, [location, crops, checkTemperatureAlerts])

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "danger"
      case "warning":
        return "warning"
      case "info":
        return "info"
      default:
        return "secondary"
    }
  }

  if (!location || !crops || crops.length === 0) return null

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <h6 className="mb-0">🌡️ Temperature Monitoring</h6>
        <div className="d-flex align-items-center gap-2">
          {monitoring && <div className="spinner-border spinner-border-sm text-primary" />}
          <Button size="sm" variant="outline-primary" onClick={checkTemperatureAlerts} disabled={monitoring}>
            Check Now
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {currentWeather && (
          <div className="mb-3 p-2 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-medium">Current Temperature:</span>
              <Badge bg="primary" className="fs-6">
                {currentWeather.avgTemp}°C
              </Badge>
            </div>
            <small className="text-muted">Last updated: {new Date(currentWeather.date).toLocaleString()}</small>
          </div>
        )}

        {temperatureAlerts.length === 0 ? (
          <Alert variant="success" className="mb-0">
            <div className="d-flex align-items-center">
              <span className="me-2">✅</span>
              <div>
                <div className="fw-medium">All crops within optimal temperature range</div>
                <small>No temperature alerts detected</small>
              </div>
            </div>
          </Alert>
        ) : (
          <div>
            <Alert variant="warning" className="mb-3">
              <div className="fw-medium">⚠️ {temperatureAlerts.length} Temperature Alert(s) Detected</div>
              <small>Some crops are outside their optimal temperature range</small>
            </Alert>

            {temperatureAlerts.map((alert, index) => (
              <Alert key={index} variant={getSeverityColor(alert.severity)} className="mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-medium">{alert.crop.name}</div>
                    <div className="small">
                      Current: {alert.currentTemp}°C | Optimal: {alert.optimalRange.min}°C - {alert.optimalRange.max}°C
                    </div>
                    <div className="small text-muted">Temperature is too {alert.type} for optimal growth</div>
                  </div>
                  <Badge bg={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                </div>
              </Alert>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default TemperatureMonitor
